import React, { useEffect, useState } from 'react';
import { Search, Plus } from 'lucide-react';
import { useToast } from '../hooks/useToast';
import { coaApi } from '../api/coa';

interface Account {
  id: number; account_code: string; account_name: string;
  type_name: string; subtype_name: string; normal_balance: string;
  requires_contact: number; is_system: number; is_active: number;
}

const typeOrder = ['Asset','Liability','Equity','Revenue','Expense'];

const badgeClass: Record<string, string> = {
  Asset: 'badge-blue', Liability: 'badge-red',
  Equity: 'badge-purple', Revenue: 'badge-green', Expense: 'badge-amber',
};

const ChartOfAccounts: React.FC = () => {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [types, setTypes] = useState<any[]>([]);
  const [subtypes, setSubtypes] = useState<any[]>([]);
  const [form, setForm] = useState({ account_code:'', account_name:'', account_type_id:'', account_subtype_id:'', description:'' });
  const { showToast } = useToast();
  const [filtered, setFiltered] = useState<Account[]>([]);
  const [search, setSearch]     = useState('');
  const [loading, setLoading]   = useState(true);

  useEffect(() => {
    coaApi.accountTypes().then(res => { setTypes(res.data.data.types); setSubtypes(res.data.data.subtypes); });
  }, []);

  useEffect(() => {
    coaApi.list().then(res => {
      setAccounts(res.data.data);
      setFiltered(res.data.data);
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    const q = search.toLowerCase();
    setFiltered(
      accounts.filter(a =>
        a.account_code.toLowerCase().includes(q) ||
        a.account_name.toLowerCase().includes(q) ||
        a.type_name.toLowerCase().includes(q)
      )
    );
  }, [search, accounts]);

  const grouped = typeOrder.reduce((acc, type) => {
    acc[type] = filtered.filter(a => a.type_name === type);
    return acc;
  }, {} as Record<string, Account[]>);

  const handleStore = async () => {
    if (!form.account_code || !form.account_name || !form.account_type_id || !form.account_subtype_id) { showToast('All fields required', 'error'); return; }
    setSaving(true);
    try {
      await coaApi.store({ ...form, account_type_id: Number(form.account_type_id), account_subtype_id: Number(form.account_subtype_id) });
      showToast('Account created!');
      setShowForm(false);
      setForm({ account_code:'', account_name:'', account_type_id:'', account_subtype_id:'', description:'' });
      coaApi.list().then(res => { setAccounts(res.data.data); setFiltered(res.data.data); });
    } catch (err: any) { showToast(err.response?.data?.message || 'Failed', 'error'); }
    finally { setSaving(false); }
  };

  return (
    <>
      <div className="page-header">
        <h1 className="page-title">Chart of Accounts</h1>
        <button className="btn btn-primary" onClick={() => setShowForm(!showForm)}><Plus size={13}/> Add Account</button>
        <button className="btn btn-primary">+ Add Account</button>
      </div>

      {showForm && (
        <div className="card" style={{marginBottom:12}}>
          <div className="card-header"><span>New Account</span><button className="btn btn-secondary btn-sm" onClick={() => setShowForm(false)}>Cancel</button></div>
          <div className="card-body">
            <div className="form-grid-2" style={{marginBottom:12}}>
              <div className="form-group"><label className="form-label">Account Code *</label><input className="form-control mono" value={form.account_code} onChange={e => setForm(f=>({...f,account_code:e.target.value}))} placeholder="e.g. 1010" /></div>
              <div className="form-group"><label className="form-label">Account Name *</label><input className="form-control" value={form.account_name} onChange={e => setForm(f=>({...f,account_name:e.target.value}))} placeholder="e.g. Bank Account" /></div>
            </div>
            <div className="form-grid-2" style={{marginBottom:12}}>
              <div className="form-group"><label className="form-label">Type *</label>
                <select className="form-control" value={form.account_type_id} onChange={e => setForm(f=>({...f,account_type_id:e.target.value,account_subtype_id:''}))}>
                  <option value="">Select type</option>
                  {types.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
              <div className="form-group"><label className="form-label">Subtype *</label>
                <select className="form-control" value={form.account_subtype_id} onChange={e => setForm(f=>({...f,account_subtype_id:e.target.value}))}>
                  <option value="">Select subtype</option>
                  {subtypes.filter(s => !form.account_type_id || String(s.account_type_id) === form.account_type_id).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
            </div>
            <div className="form-group" style={{marginBottom:12}}><label className="form-label">Description</label><input className="form-control" value={form.description} onChange={e => setForm(f=>({...f,description:e.target.value}))} /></div>
            <button className="btn btn-primary" onClick={handleStore} disabled={saving}>{saving ? 'Saving...' : 'Create Account'}</button>
          </div>
        </div>
      )}

      <div className="filter-bar">
        <div className="search-box">
          <Search size={13} color="var(--tx3)" />
          <input
            placeholder="Search accounts..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <span className="record-count">{filtered.length} accounts</span>
      </div>

      {loading ? (
        <div className="empty-state"><p>Loading...</p></div>
      ) : (
        typeOrder.map(type => grouped[type]?.length > 0 && (
          <div className="card" key={type} style={{ marginBottom: 12 }}>
            <div className="card-header">
              <span>{type}</span>
              <span className="record-count">{grouped[type].length} accounts</span>
            </div>
            <table>
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Account Name</th>
                  <th>Subtype</th>
                  <th>Normal Balance</th>
                  <th>Type</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {grouped[type].map(account => (
                  <tr key={account.id}>
                    <td className="td-code">{account.account_code}</td>
                    <td>{account.account_name}</td>
                    <td style={{ color: 'var(--tx3)' }}>{account.subtype_name}</td>
                    <td><span className={`badge ${account.normal_balance === 'debit' ? 'badge-blue' : 'badge-green'}`}>{account.normal_balance}</span></td>
                    <td><span className={`badge ${badgeClass[account.type_name]}`}>{account.type_name}</span></td>
                    <td><span className={`badge ${account.is_active ? 'badge-green' : 'badge-gray'}`}>{account.is_active ? 'Active' : 'Inactive'}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))
      )}
    </>
  );
};

export default ChartOfAccounts;
